import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { FaRobot, FaCogs, FaCodeBranch, FaLightbulb, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../../firebase/config';
import Spinner from '../Spinner';
import './Services.css';

const Services = () => {
  const { t, i18n } = useTranslation();
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const [services, setServices] = useState([]);
  const [rawData, setRawData] = useState([]); // Store raw Firestore data
  const [loading, setLoading] = useState(true);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const scrollContainerRef = useRef(null);

  // Get current language (default to 'en' if not 'fr')
  const currentLang = i18n.language?.startsWith('fr') ? 'fr' : 'en';

  // Default/fallback services (used if Firestore is empty or fails)
  const defaultServices = [
    {
      icon: <FaRobot />,
      title: t('services.rpa.title'),
      description: t('services.rpa.description'),
      features: t('services.rpa.features', { returnObjects: true }),
      image: "/assets/images/solar-energy.jpg"
    },
    {
      icon: <FaCogs />,
      title: t('services.robotics.title'),
      description: t('services.robotics.description'),
      image: "/assets/images/ai-technology.jpg"
    },
    {
      icon: <FaCodeBranch />,
      title: t('services.lowcode.title'),
      description: t('services.lowcode.description'),
      image: "/assets/images/data-analytics.jpg"
    }
  ];

  // Fetch data from Firestore once
  useEffect(() => {
    const fetchExpertise = async () => {
      try {
        const q = query(collection(db, 'expertise'), orderBy('order', 'asc'));
        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
          const items = querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          setRawData(items);
        } else {
          setRawData([]);
        }
      } catch (err) {
        console.log('Using default services (Firestore not configured or empty)');
        setRawData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchExpertise();
  }, []);

  // Update services when language changes or rawData updates
  useEffect(() => {
    if (rawData.length > 0) {
      const localizedItems = rawData.map(item => {
        // Get title based on language, fallback to English or legacy field
        const title = currentLang === 'fr'
          ? (item.title_fr || item.title_en || item.title)
          : (item.title_en || item.title);

        // Get description based on language
        const description = currentLang === 'fr'
          ? (item.description_fr || item.description_en || item.description)
          : (item.description_en || item.description);

        // Get features based on language
        const features = currentLang === 'fr'
          ? (item.features_fr?.length ? item.features_fr : (item.features_en || item.features || []))
          : (item.features_en?.length ? item.features_en : (item.features || []));

        return {
          id: item.id,
          icon: <FaLightbulb />,
          title,
          description,
          features,
          image: item.imageUrl || "/assets/images/solar-energy.jpg"
        };
      });
      setServices(localizedItems);
    } else if (!loading) {
      setServices(defaultServices);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawData, currentLang, loading]);

  if (loading) {
    return (
      <section id="services" className="services">
        <div className="container">
          <Spinner size="medium" text={t('services.loading')} />
        </div>
      </section>
    );
  }

  const isHorizontalScroll = services.length > 3;

  const checkScrollButtons = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  const scroll = (direction) => {
    if (scrollContainerRef.current) {
      const scrollAmount = 380; // Card width + gap
      scrollContainerRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
      setTimeout(checkScrollButtons, 300);
    }
  };

  const servicesContent = (
    <div className={`services-grid ${isHorizontalScroll ? 'horizontal-scroll' : ''}`}>
      {services.map((service, index) => (
        <motion.div
          key={service.id || index}
          className="service-card"
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: Math.min(index * 0.15, 0.6) }}
        >
          <div className="service-image">
            <img src={service.image} alt={service.title} />
            <div className="service-overlay">
              <div className="service-icon">
                {service.icon}
              </div>
            </div>
          </div>

          <div className="service-content">
            <h3>{service.title}</h3>
            <p>{service.description}</p>

            {service.features && service.features.length > 0 && (
              <ul className="service-features">
                {service.features.map((feature, idx) => (
                  <li key={idx}>{feature}</li>
                ))}
              </ul>
            )}
          </div>
        </motion.div>
      ))}
    </div>
  );

  return (
    <section id="services" className="services" ref={ref}>
      <div className="container">
        <motion.h2
          className="section-title"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          {t('services.title')}
        </motion.h2>

        {isHorizontalScroll ? (
          <div className="services-scroll-wrapper">
            <button
              className="scroll-arrow left"
              onClick={() => scroll('left')}
              disabled={!canScrollLeft}
              aria-label="Scroll left"
            >
              <FaChevronLeft />
            </button>

            <div
              className="services-scroll-container"
              ref={scrollContainerRef}
              onScroll={checkScrollButtons}
            >
              {servicesContent}
            </div>

            <button
              className="scroll-arrow right"
              onClick={() => scroll('right')}
              disabled={!canScrollRight}
              aria-label="Scroll right"
            >
              <FaChevronRight />
            </button>
          </div>
        ) : (
          servicesContent
        )}
      </div>
    </section>
  );
};

export default Services;